import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
// Logo will be loaded from public folder

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const LoginForm = () => {
  const navigate = useNavigate()
  const { signIn } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const { register, handleSubmit, formState: { errors }, setError, clearErrors } = form

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setSubmitError(null)
    clearErrors()
    
    try {
      const { error } = await signIn(data.email, data.password)
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('email', { 
            type: 'manual', 
            message: 'Invalid email or password' 
          })
          setError('password', { 
            type: 'manual', 
            message: 'Invalid email or password' 
          })
        } else if (error.message.includes('Email not confirmed')) {
          setSubmitError('Please verify your email address before signing in.')
        } else {
          setSubmitError(error.message)
        }
      } else {
        navigate('/')
      }
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full">
      {/* Title */}
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
        Welcome Back
      </h1>

      {/* Submit Error Display */}
      {submitError && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-xs sm:text-sm text-center">{submitError}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Email
          </label>
          <input
            {...register('email')}
            type="email"
            id="email"
            className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base"
            placeholder="Enter your email"
          />
          {errors.email && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-600 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="w-full px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10 text-sm sm:text-base"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between text-xs sm:text-sm">
          <label className="flex items-center">
            <input type="checkbox" className="rounded border-gray-300 bg-white text-red-600 focus:ring-red-500 w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="ml-2 text-gray-600">Remember me</span>
          </label>
          <Link to="/forgot-password" className="text-red-600 hover:text-red-500">
            Forgot password?
          </Link>
        </div>

        {/* Login Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-semibold py-2.5 sm:py-3 px-4 rounded-full transition-colors duration-200 text-sm sm:text-base"
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </button>

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-gray-600 text-xs sm:text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="text-red-600 hover:text-red-500 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </form>
    </div>
  )
}
